import { FC } from "react";
import { IGetProductResponse } from "../../shared/services/stripe/interfaces/get-products.interfacae";
import { formatPriceInteger } from "../../shared/utils/format-price";
import pricingImg from "../../../assets/images/pricingbanner.png";
import styles from "./SubscriptionProducts.module.scss";
import {
    getFormattedSubscriptionType,
    isYearlySubscription,
} from "../../shared/utils/utils";

interface IProps {
    products: IGetProductResponse[];
    role: string | undefined;
    showSelectButtons?: boolean;
    isFreeTrialEnded?: boolean;
    showHeaderWording?: boolean;
    onSelectProduct?: (value: IGetProductResponse) => void;
}

const SubscriptionProducts: FC<IProps> = ({
    products,
    role,
    showSelectButtons = true,
    isFreeTrialEnded = false,
    showHeaderWording,
    onSelectProduct,
}) => {
    const getHeaderWording = () => {
        return (
            <>
                <span
                    className={`${styles.trialEndedTitle} text-aha-gray font-century-gothic-bold`}
                >
                    Looks like your premium access has ended.
                </span>
                <p className={`${styles.trialEndedDesc} text-aha-gray`}>
                    Pick a plan to keep the momentum going!
                </p>
            </>
        );
    };

    return (
        <div className="flex-1 bg-aha-green">
            <div className={`bg-aha-green-dark ${styles.titleContainer}`}>
                {(showHeaderWording || isFreeTrialEnded) && getHeaderWording()}

                <div className={styles.bannerContainer}>
                    <img
                        src={pricingImg}
                        alt="Pricing Banner"
                        className={styles.pricingBanner}
                    />
                </div>
            </div>
            <div className={styles.cardsContainer}>
                {products?.length ? (
                    <>
                        {products?.map?.((p) => {
                            const { product, prices } = p;
                            const isYearly = isYearlySubscription(
                                product.subscriptionType
                            );
                            const isParentYearly =
                                role === "PARENT" && isYearly;

                            return (
                                <div
                                    key={product.id}
                                    className={styles.cardContainer}
                                >
                                    <div
                                        className={`bg-white text-aha-black rounded-md ${styles.card}`}
                                    >
                                        <div
                                            className={`text-aha-orange font-century-gothic-bold ${styles.productName}`}
                                        >
                                            {getFormattedSubscriptionType(
                                                product.subscriptionType
                                            )}
                                        </div>
                                        <div
                                            className={`text-aha-gray-dark ${styles.productPrice}`}
                                        >
                                            {formatPriceInteger(
                                                prices[0].unit_amount
                                            )}
                                            {isParentYearly && (
                                                <span
                                                    className={`${styles.discount}`}
                                                >
                                                    (17% discount)
                                                </span>
                                            )}
                                        </div>
                                        {isParentYearly && (
                                            <span
                                                className={`text-sm text-aha-secondary-text ${styles.productDescriptionParent}`}
                                            >
                                                One year of speech support--for
                                                the price of 1-2 private
                                                sessions.
                                            </span>
                                        )}

                                        {role === "CLINICIAN" && (
                                            <span
                                                className={`text-sm text-aha-secondary-text ${styles.productDescriptionClinician}`}
                                            >
                                                - Includes free, unlimited
                                                student licenses <br /> (yes,
                                                you read that right!)
                                            </span>
                                        )}
                                    </div>

                                    {showSelectButtons && (
                                        <button
                                            className={`bg-aha-black text-white rounded-md ${styles.productBtn}`}
                                            onClick={() => {
                                                onSelectProduct &&
                                                    onSelectProduct(p);
                                            }}
                                        >
                                            Subscribe now
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                        {role === "PARENT" && (
                            <div className="text-center pb-2 pt-2 space-y-4 text-white">
                                <div className="text-2xl uppercase font-century-gothic-bold tracking-wider">
                                    purchase subscription
                                </div>
                                <div>
                                    Select a subscription offer to
                                    <br /> unlock{" "}
                                    <span className="font-century-gothic-bold">
                                        FULL ACCESS
                                    </span>{" "}
                                    to
                                    <br /> At Home Articulation
                                </div>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex flex-col gap-2 justify-center items-center h-full my-4 text-xl text-white">
                        <p>
                            No subscriptions available at the moment. Contact
                            support for plans at this email:
                        </p>
                        <p>
                            <a href="mailto:support@athomearticulation.com">
                                support@athomearticulation.com
                            </a>
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscriptionProducts;
